/**
 * Allys Property Hero Component
 * 
 * Airbnb-style image gallery with grid layout.
 * Supports featured content overlays for prominently featured properties.
 * Adapts layout based on number of available images.
 */

'use client';

import type { MediaResponseDto } from '@/lib/api/clients/properties';
import type { FeaturedPlacement, FeaturedPlacementSummary, FeaturedBadgeVariant } from '@/lib/api/clients/featured-content';
import { getPrimaryFeaturedPlacement } from '@/lib/api/clients/featured-content';
import { FeaturedBadge } from '@/components/listings/FeaturedBadge';
import Image from 'next/image';
import { useState } from 'react';

export interface PropertyHeroProps {
  title: string;
  primaryImage?: string | null;
  images?: MediaResponseDto[];
  className?: string;
  /** Featured placements for this property */
  featuredPlacements?: (FeaturedPlacement | FeaturedPlacementSummary)[];
  /** Whether to show the featured badge overlay */
  showFeaturedBadge?: boolean;
}

type HeroMediaItem = {
  id: string;
  url: string;
  videoUrl?: string | undefined;
  mediaType?: string | undefined;
  mimeType?: string | undefined;
};

function isVideoMedia(item: HeroMediaItem): boolean {
  return item.mediaType === 'VIDEO' || !!item.mimeType?.startsWith('video/') || !!item.videoUrl;
}

function HeroMediaCell({
  item,
  alt,
  fill = true,
  priority = false,
  sizes,
  className = 'object-cover',
}: {
  item: HeroMediaItem;
  alt: string;
  fill?: boolean;
  priority?: boolean;
  sizes?: string;
  className?: string;
}) {
  if (isVideoMedia(item)) {
    return (
      <video
        src={item.videoUrl || item.url}
        autoPlay
        muted
        loop
        playsInline
        className={`${fill ? 'absolute inset-0 w-full h-full' : ''} ${className}`}
      />
    );
  }
  return (
    <Image
      src={item.url}
      alt={alt}
      fill={fill}
      className={className}
      priority={priority}
      sizes={sizes}
    />
  );
}

export function AllysPropertyHero({
  title,
  primaryImage,
  images = [],
  featuredPlacements = [],
  showFeaturedBadge = true,
}: PropertyHeroProps) {
  const [showGallery, setShowGallery] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  // Combine primary image with gallery images
  const allImages: HeroMediaItem[] = [
    ...(primaryImage ? [{ id: 'primary', url: primaryImage }] : []),
    ...images.map((img) => ({
      id: img.id,
      url: img.url,
      mediaType: img.mediaType,
      mimeType: img.mimeType,
      videoUrl: (img.mediaType === 'VIDEO' || img.mimeType?.startsWith('video/')) ? img.url : undefined,
    })),
  ];
    
  // Get primary featured badge
  const primaryFeatured = showFeaturedBadge && featuredPlacements.length > 0
    ? getPrimaryFeaturedPlacement(featuredPlacements)
    : null;

  if (allImages.length === 0) {
    return (
      <div className="relative w-full h-[400px] md:h-[500px] bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center pt-20">
        <div className="text-center">
          <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <p className="text-gray-400 text-lg font-inter">
            No images available
          </p>
        </div>
      </div>
    );
  }

  const mainImage = allImages[0]!;
  const gridImages = allImages.slice(1, 5);
  const totalImages = allImages.length;

  const handlePrevImage = () => {
    setCurrentImageIndex((prev) => 
      prev === 0 ? allImages.length - 1 : prev - 1
    );
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prev) => 
      prev === allImages.length - 1 ? 0 : prev + 1
    );
  };

  // Single image layout - full width hero
  if (totalImages === 1) {
    return (
      <>
        <div className="relative w-full max-w-screen-2xl mx-auto px-6 lg:px-20 pt-24 pb-6">
          {/* Featured Badge Overlay */}
          {primaryFeatured?.badgeVariant && (
            <div className="absolute top-28 left-10 lg:left-24 z-20">
              <FeaturedBadge
                variant={primaryFeatured.badgeVariant as FeaturedBadgeVariant}
                label={primaryFeatured.label}
                size="lg"
              />
            </div>
          )}
          
          <div 
            className="relative w-full h-[400px] md:h-[500px] lg:h-[600px] rounded-xl overflow-hidden cursor-pointer group"
            onClick={() => setShowGallery(true)}
          >
            <HeroMediaCell
              item={mainImage}
              alt={title}
              priority
              sizes="100vw"
              className="object-cover transition-transform duration-500 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          </div>
        </div>

        {/* Gallery Modal */}
        {showGallery && renderGalleryModal()}
      </>
    );
  }

  // 2-3 images - simple side by side layout
  if (totalImages >= 2 && totalImages <= 3) {
    return (
      <>
        <div className="relative w-full max-w-screen-2xl mx-auto px-6 lg:px-20 pt-24 pb-6">
          {/* Featured Badge Overlay */}
          {primaryFeatured?.badgeVariant && (
            <div className="absolute top-28 left-10 lg:left-24 z-20">
              <FeaturedBadge
                variant={primaryFeatured.badgeVariant as FeaturedBadgeVariant}
                label={primaryFeatured.label}
                size="lg"
              />
            </div>
          )}
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 h-[400px] md:h-[480px] rounded-xl overflow-hidden">
            {/* Main large image */}
            <div 
              className="md:col-span-2 relative cursor-pointer overflow-hidden group"
              onClick={() => {
                setCurrentImageIndex(0);
                setShowGallery(true);
              }}
            >
              <HeroMediaCell
                item={mainImage}
                alt={title}
                priority
                sizes="(max-width: 768px) 100vw, 66vw"
                className="object-cover transition-transform duration-300 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-all duration-300" />
            </div>

            {/* Side images */}
            <div className="hidden md:flex flex-col gap-2">
              {gridImages.slice(0, 2).map((image, index) => (
                <div
                  key={image.id}
                  className="relative flex-1 cursor-pointer overflow-hidden group"
                  onClick={() => {
                    setCurrentImageIndex(index + 1);
                    setShowGallery(true);
                  }}
                >
                  <HeroMediaCell
                    item={image}
                    alt={`${title} - ${index + 2}`}
                    sizes="33vw"
                    className="object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-all duration-300" />
                  
                  {/* Show all photos button on last image */}
                  {index === gridImages.slice(0, 2).length - 1 && (
                    <div className="absolute bottom-4 right-4">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowGallery(true);
                        }}
                        className="px-4 py-2 bg-white/95 backdrop-blur-sm border border-gray-200 rounded-lg text-sm font-semibold hover:bg-white transition-all duration-200 shadow-lg flex items-center gap-2 font-inter"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        {totalImages} photos
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Gallery Modal */}
        {showGallery && renderGalleryModal()}
      </>
    );
  }

  // 4+ images - full Airbnb-style grid
  return (
    <>
      {/* Airbnb-Style Grid Layout */}
      <div className="relative w-full max-w-screen-2xl mx-auto px-6 lg:px-20 pt-24 pb-6">
        {/* Featured Badge Overlay - Top Left */}
        {primaryFeatured?.badgeVariant && (
          <div className="absolute top-28 left-10 lg:left-24 z-20">
            <FeaturedBadge
              variant={primaryFeatured.badgeVariant as FeaturedBadgeVariant}
              label={primaryFeatured.label}
              size="lg"
            />
          </div>
        )}
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2 h-[400px] md:h-[480px] rounded-xl overflow-hidden">
          {/* Main large image - left side */}
          <div 
            className="md:col-span-2 relative cursor-pointer overflow-hidden group"
            onClick={() => {
              setCurrentImageIndex(0);
              setShowGallery(true);
            }}
          >
            <HeroMediaCell
              item={mainImage}
              alt={title}
              priority
              sizes="(max-width: 768px) 100vw, 50vw"
              className="object-cover transition-transform duration-300 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-all duration-300" />
          </div>

          {/* Grid of smaller images - right side */}
          <div className="hidden md:grid md:col-span-2 grid-cols-2 gap-2">
            {Array.from({ length: 4 }).map((_, index) => {
              // Use actual grid images if available
              const image = gridImages[index];
              if (!image) return null;
              
              const isLastImage = index === 3 || index === gridImages.length - 1;
              
              return (
                <div
                  key={image.id + '-' + index}
                  className="relative cursor-pointer overflow-hidden group"
                  onClick={() => {
                    setCurrentImageIndex(index + 1);
                    setShowGallery(true);
                  }}
                >
                  <HeroMediaCell
                    item={image}
                    alt={`${title} - ${index + 2}`}
                    sizes="25vw"
                    className="object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-all duration-300" />
                  
                  {/* Show all photos button - overlay on last image */}
                  {isLastImage && totalImages > 5 && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowGallery(true);
                        }}
                        className="px-5 py-3 bg-white border border-[#222222] rounded-lg text-sm font-semibold hover:bg-gray-50 transition-all duration-200 shadow-lg flex items-center gap-2 z-10 font-inter"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        +{totalImages - 5} more
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        
        {/* View all button for 4-5 images */}
        {totalImages >= 4 && totalImages <= 5 && (
          <div className="absolute bottom-10 right-10 lg:right-24 z-10">
            <button
              onClick={() => setShowGallery(true)}
              className="px-5 py-3 bg-white border border-[#222222] rounded-lg text-sm font-semibold hover:bg-gray-50 transition-all duration-200 shadow-lg flex items-center gap-2 font-inter"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              View all {totalImages} photos
            </button>
          </div>
        )}
      </div>

      {/* Gallery Modal */}
      {showGallery && renderGalleryModal()}
    </>
  );

  function renderGalleryModal() {
    return (
      <div 
        className="fixed inset-0 z-[9999] bg-black"
        onClick={() => setShowGallery(false)}
      >
        {/* Close Button */}
        <button
          onClick={() => setShowGallery(false)}
          className="absolute top-6 right-6 z-50 p-3 rounded-full bg-white/10 backdrop-blur-sm hover:bg-white/20 transition-all duration-200 text-white"
          aria-label="Close gallery"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Image Counter */}
        <div 
          className="absolute top-6 left-6 z-50 px-4 py-2 rounded-lg bg-white/10 backdrop-blur-sm text-white text-sm font-medium font-inter"
        >
          {currentImageIndex + 1} / {allImages.length}
        </div>

        {/* Navigation Arrows */}
        {allImages.length > 1 && (
          <>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handlePrevImage();
              }}
              className="absolute left-6 top-1/2 -translate-y-1/2 z-50 p-3 rounded-full bg-white/10 backdrop-blur-sm hover:bg-white/20 transition-all duration-200 text-white"
              aria-label="Previous image"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleNextImage();
              }}
              className="absolute right-6 top-1/2 -translate-y-1/2 z-50 p-3 rounded-full bg-white/10 backdrop-blur-sm hover:bg-white/20 transition-all duration-200 text-white"
              aria-label="Next image"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </>
        )}

        {/* Current Image or Video */}
        <div 
          className="relative w-full h-full flex items-center justify-center p-12"
          onClick={(e) => e.stopPropagation()}
        >
          {isVideoMedia(allImages[currentImageIndex]!) ? (
            <video
              key={currentImageIndex}
              src={allImages[currentImageIndex]!.videoUrl || allImages[currentImageIndex]!.url}
              controls
              autoPlay
              playsInline
              className="max-w-full max-h-full"
            />
          ) : (
            <Image
              src={allImages[currentImageIndex]!.url}
              alt={`${title} - ${currentImageIndex + 1}`}
              fill
              className="object-contain"
              sizes="100vw"
              priority
            />
          )}
        </div>
      </div>
    );
  }
}
